import { Injectable } from '@angular/core';
import { CustomCart, CustomOrderEntry } from '@cart/const';
import { getCartIdByUserId } from '@cart/utils';
import { Store } from '@ngrx/store';
import { PriceKind, SelectedPrice, Unit } from '@product/const';
import { SelectedPriceService } from '@product/data-access';
import { convertToSaleUnit } from '@product/utils';
import { OCC_CART_ID_CURRENT, OCC_USER_ID_ANONYMOUS, UserIdService } from '@spartacus/core';
import { Observable, Subject, combineLatest, concatMap, debounceTime, of, withLatestFrom } from 'rxjs';
import { filter, map, startWith, take } from 'rxjs/operators';
import { StateWithMultiCart } from '../store';
import { CartActions } from '../store/actions';
import { ActiveCartService } from './active-cart.service';
import { CloneCartFacade } from './clone-cart.facade';
import { MultiCartService } from './multi-cart.service';

export interface EntryProps {
  isTransit?: boolean;
  updateableOnly?: boolean;
}

@Injectable({
  providedIn: 'root',
})
export class CustomActiveCartService extends ActiveCartService {
  loading$: Observable<boolean> = combineLatest({ isStable: this.isStable(), cart: this.getActive() }).pipe(
    map(({ isStable, cart }) => !isStable && !this.isCartEmpty(cart)),
  );

  private updateEntrySubjects = new Map<string, Subject<number>>();

  constructor(
    store: Store<StateWithMultiCart>,
    multiCartService: MultiCartService,
    userIdService: UserIdService,
    private selectedPriceService: SelectedPriceService,
    private cloneCartFacade: CloneCartFacade,
  ) {
    super(store, multiCartService, userIdService);
  }

  refreshCurrentCart(): void {
    combineLatest({ cartId: this.getActiveCartId(), userId: this.userIdService.getUserId() })
      .pipe(
        take(1),
        filter(({ cartId, userId }) => Boolean(userId) && Boolean(cartId)),
      )
      .subscribe(({ cartId, userId }) => {
        if (!(userId === OCC_USER_ID_ANONYMOUS && cartId === OCC_CART_ID_CURRENT)) {
          this.multiCartService.loadCart({
            userId,
            cartId,
            extraData: {
              active: true,
            },
          });
        }
      });
  }

  getUpdateableEntry(productCode: string): Observable<CustomOrderEntry> {
    return this.getEntry(productCode).pipe(map((entry: CustomOrderEntry) => (entry.updateable ? entry : undefined)));
  }

  /**
   *
   * @param entryProps
   *  - isTransit - Если undefined, то ищется по текущему выбранному isTransit для товара
   *  - updateableOnly: Возвращать только изменяемые энтри
   */
  getEntry(productCode: string, entryProps?: EntryProps): Observable<CustomOrderEntry | undefined> {
    const { isTransit: propsIsTransit, updateableOnly = true } = entryProps || {};

    return combineLatest({
      selectedPrice: this.selectedPriceService.getSelectedPrice(productCode),
      isTransit: propsIsTransit
        ? of(propsIsTransit)
        : this.selectedPriceService.getSelectedPrice(productCode).pipe(map(({ kind }) => kind === PriceKind.TRANSIT)),
      entries: this.getEntries(),
    }).pipe(
      map(({ selectedPrice, isTransit, entries }) => {
        if (!entries || entries.length === 0) {
          return undefined;
        }

        const saleUnit: Unit = convertToSaleUnit(selectedPrice);

        const matchEntry: CustomOrderEntry | undefined = (entries as CustomOrderEntry[])
          .filter(
            (entry: CustomOrderEntry) => productCode === entry.product.code && entry.saleUnit.code === saleUnit.code,
          )
          .filter((entry: CustomOrderEntry) => {
            // проверка на изменяемость
            if (updateableOnly) {
              return entry?.updateable;
            }

            return true;
          })
          .find((entry: CustomOrderEntry) => entry.transit === Boolean(isTransit));

        return matchEntry;
      }),
    );
  }

  getMaxQuantityByEntry(productCode: string, entryProps?: EntryProps): Observable<number> {
    return this.getEntry(productCode, entryProps).pipe(
      map((entry: CustomOrderEntry) => entry?.product?.stock?.stockLevel),
      filter((quantity: number) => quantity !== undefined),
      startWith(999999),
    );
  }

  setActiveCartId(cartId: string): void {
    this.store.dispatch(new CartActions.SetActiveCartId(cartId));
  }

  changeActiveCart(cartId: string): void {
    this.store.dispatch(new CartActions.ChangeActiveCart(cartId));
  }

  isCurrentCartEmpty(): Observable<boolean> {
    return this.getActive().pipe(map((cart) => !cart?.entries?.length));
  }

  debouncedUpdateEntry(entryNumber: string, quantity: number): void {
    const entrySubject = this.getOrCreateEntrySubject(entryNumber);
    entrySubject.next(quantity);
  }

  // метод, который ставит склонированную корзину как основную
  setActiveClonnedCart(): void {
    combineLatest({ cart: this.cloneCartFacade.getClonedCartValue(), userId: this.userIdService.getUserId() })
      .pipe(take(1))
      .subscribe(({ cart, userId }) => {
        if (cart) {
          this.setActiveCartId(getCartIdByUserId(cart, userId));
          this.cloneCartFacade.resetClonedCartValue();
        }
      });
  }

  restoreEntry(entry: CustomOrderEntry): void {
    const selectedPrice: SelectedPrice = {
      ...entry.basePrice,
      unit: entry.saleUnit,
      saleUnit: entry.saleUnit,
      kind: entry.transit ? PriceKind.TRANSIT : PriceKind.REGULAR,
    };

    this.requireLoadedCart()
      .pipe(withLatestFrom(this.userIdService.getUserId()))
      .subscribe(([cartState, userId]) => {
        const cartId = getCartIdByUserId(cartState.value, userId);
        this.store.dispatch(
          new CartActions.CartAddEntry({
            userId,
            cartId,
            productCode: entry.product.code,
            quantity: entry.quantity || 1,
            selectedPrice,
          }),
        );
      });
  }

  // TODO Заменить на родительский метод
  // https://it-labs.atlassian.net/browse/HYB2-3630
  protected isCartEmpty(cart: CustomCart): boolean {
    return !cart || (typeof cart === 'object' && Object.keys(cart).length === 0);
  }

  private getOrCreateEntrySubject(entryNumber: string): Subject<number> {
    let entrySubject = this.updateEntrySubjects.get(entryNumber);

    if (!entrySubject) {
      entrySubject = new Subject<number>();

      entrySubject
        .pipe(
          debounceTime(700),
          withLatestFrom(this.activeCartId$, this.userIdService.getUserId()),
          concatMap(([quantity, cartId, userId]) => {
            if (quantity === 0) {
              this.multiCartService.removeEntry(userId, cartId, entryNumber);
            } else {
              this.multiCartService.updateEntry(userId, cartId, entryNumber, quantity);
            }

            return this.isStable().pipe(filter(Boolean), take(1));
          }),
        )
        .subscribe();

      this.updateEntrySubjects.set(entryNumber, entrySubject);
    }

    return entrySubject;
  }
}
